import { InputHTMLAttributes, forwardRef } from 'react';

/**
 * Input — single-line text input + variants.
 * Use TextArea atom (separate component) for multi-line.
 *
 * Phase 11 Block 6 PR 2.
 */

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  invalid?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, size = 'md', style, ...rest },
  ref,
) {
  const padY = size === 'sm' ? '6px' : size === 'lg' ? '10px' : '8px';
  const padX = size === 'sm' ? '10px' : '12px';
  const fs = size === 'sm' ? 'var(--fs-sm)' : size === 'lg' ? 'var(--fs-lg)' : 'var(--fs-md)';
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      style={{
        fontFamily: 'inherit',
        fontSize: fs,
        padding: `${padY} ${padX}`,
        border: `1px solid ${invalid ? 'var(--color-status-red)' : 'var(--color-border)'}`,
        borderRadius: 'var(--r-md)',
        background: 'var(--color-card)',
        color: 'var(--color-ink)',
        outline: 'none',
        transition: 'border-color 120ms ease',
        width: '100%',
        // Constrain to the container. A flex/grid parent gives its children
        // `min-width:auto` (they won't shrink below content), and iOS renders a
        // native <input type="date"> at an intrinsic min-width that then
        // overflows a narrow pop-up or filter row. min-width:0 + max-width let it
        // shrink to fit (box-sizing:border-box is already global via reset.css).
        minWidth: 0,
        maxWidth: '100%',
        ...style,
      }}
      {...rest}
    />
  );
});
